/**
 * Inline Lucide icons.
 *
 * Hand-extracted from `lucide-static` (24px, 2px stroke, currentColor). We
 * inline the path data instead of depending on the runtime `lucide` package
 * to keep the bundle tiny: a whitelist of ~13 glyphs costs ~2KB total. If a
 * game needs an icon outside this list, add it here (do NOT pull the full
 * runtime library into the components package).
 *
 * Source: https://lucide.dev/icons/
 */

export type LucideName =
  | 'x'
  | 'copy'
  | 'upload'
  | 'sun'
  | 'moon'
  | 'monitor'
  | 'info'
  | 'log-in'
  | 'circle-question-mark'
  | 'arrow-right'
  | 'arrow-up'
  | 'chevron-left'
  | 'chevron-right'
  | 'chevron-up'
  | 'grid'
  | 'shuffle'
  | 'undo'
  | 'flame'
  | 'eye'
  | 'eye-off'
  | 'delete'
  | 'corner-down-left'
  | 'star'
  | 'chevron-down'
  | 'keyboard'
  | 'flag'
  | 'bomb'
  | 'alarm-clock'
  | 'timer'
  | 'circle-dot'
  | 'square'
  | 'grid-3x3'
  | 'calculator'
  | 'spline'
  | 'lightbulb'
  | 'minus'
  | 'target'
  | 'crosshair'
  | 'anchor'
  | 'trash-2'
  | 'rotate-cw'
  | 'trophy'
  | 'audio-lines'
  | 'volume-2'
  | 'volume-x'
  | 'play'
  | 'quote'
  | 'check'
  | 'circle-check'
  | 'hash'
  | 'sigma';

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Inner path data per icon. All draw inside the standard 24x24 viewBox. */
const ICON_PATHS: Record<LucideName, string> = {
  x: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
  copy:
    '<rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>' +
    '<path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>',
  upload:
    '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>' +
    '<polyline points="17 8 12 3 7 8"/>' +
    '<line x1="12" x2="12" y1="3" y2="15"/>',
  sun:
    '<circle cx="12" cy="12" r="4"/>' +
    '<path d="M12 2v2"/><path d="M12 20v2"/>' +
    '<path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/>' +
    '<path d="M2 12h2"/><path d="M20 12h2"/>' +
    '<path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/>',
  moon: '<path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>',
  monitor:
    '<rect width="20" height="14" x="2" y="3" rx="2"/>' +
    '<line x1="8" x2="16" y1="21" y2="21"/>' +
    '<line x1="12" x2="12" y1="17" y2="21"/>',
  info:
    '<circle cx="12" cy="12" r="10"/>' +
    '<path d="M12 16v-4"/><path d="M12 8h.01"/>',
  'log-in':
    '<path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>' +
    '<polyline points="10 17 15 12 10 7"/>' +
    '<line x1="15" x2="3" y1="12" y2="12"/>',
  'circle-question-mark':
    '<circle cx="12" cy="12" r="10"/>' +
    '<path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>' +
    '<path d="M12 17h.01"/>',
  'arrow-right':
    '<path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>',
  'arrow-up':
    '<path d="m5 12 7-7 7 7"/><path d="M12 19V5"/>',
  'chevron-left': '<path d="m15 18-6-6 6-6"/>',
  'chevron-right': '<path d="m9 18 6-6-6-6"/>',
  'chevron-up': '<path d="m18 15-6-6-6 6"/>',
  grid:
    '<rect width="7" height="7" x="3" y="3" rx="1"/>' +
    '<rect width="7" height="7" x="14" y="3" rx="1"/>' +
    '<rect width="7" height="7" x="14" y="14" rx="1"/>' +
    '<rect width="7" height="7" x="3" y="14" rx="1"/>',
  shuffle:
    '<path d="M2 18h1.4c1.3 0 2.5-.6 3.3-1.7l6.1-8.6c.7-1.1 2-1.7 3.3-1.7H22"/>' +
    '<path d="m18 2 4 4-4 4"/>' +
    '<path d="M2 6h1.9c1.5 0 2.9.9 3.6 2.2"/>' +
    '<path d="M22 18h-5.9c-1.3 0-2.6-.7-3.3-1.8l-.5-.8"/>' +
    '<path d="m18 14 4 4-4 4"/>',
  undo:
    '<path d="M3 7v6h6"/>' +
    '<path d="M21 17a9 9 0 0 0-15-6.7L3 13"/>',
  flame:
    '<path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>',
  eye:
    '<path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/>' +
    '<circle cx="12" cy="12" r="3"/>',
  'eye-off':
    '<path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"/>' +
    '<path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"/>' +
    '<path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"/>' +
    '<path d="m2 2 20 20"/>',
  delete:
    '<path d="M20 5H9l-7 7 7 7h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2Z"/>' +
    '<path d="m18 9-6 6"/>' +
    '<path d="m12 9 6 6"/>',
  'corner-down-left':
    '<polyline points="9 10 4 15 9 20"/>' +
    '<path d="M20 4v7a4 4 0 0 1-4 4H4"/>',
  star:
    '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
  'chevron-down': '<path d="m6 9 6 6 6-6"/>',
  keyboard:
    '<rect width="20" height="16" x="2" y="4" rx="2" ry="2"/>' +
    '<path d="M6 8h.001"/><path d="M10 8h.001"/><path d="M14 8h.001"/><path d="M18 8h.001"/>' +
    '<path d="M8 12h.001"/><path d="M12 12h.001"/><path d="M16 12h.001"/>' +
    '<path d="M7 16h10"/>',
  flag:
    '<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>' +
    '<line x1="4" x2="4" y1="22" y2="15"/>',
  bomb:
    '<circle cx="11" cy="13" r="9"/>' +
    '<path d="M14.35 4.65 16.3 2.7a2.41 2.41 0 0 1 3.4 0l1.6 1.6a2.4 2.4 0 0 1 0 3.4l-1.95 1.95"/>' +
    '<path d="m22 2-1.5 1.5"/>',
  'alarm-clock':
    '<circle cx="12" cy="13" r="8"/>' +
    '<path d="M12 9v4l2 2"/>' +
    '<path d="M5 3 2 6"/>' +
    '<path d="m22 6-3-3"/>' +
    '<path d="M6.38 18.7 4 21"/>' +
    '<path d="M17.64 18.67 20 21"/>',
  timer:
    '<line x1="10" x2="14" y1="2" y2="2"/>' +
    '<line x1="12" x2="15" y1="14" y2="11"/>' +
    '<circle cx="12" cy="14" r="8"/>',
  'circle-dot':
    '<circle cx="12" cy="12" r="10"/>' +
    '<circle cx="12" cy="12" r="2.5" fill="currentColor"/>',
  square: '<rect width="18" height="18" x="3" y="3" rx="2"/>',
  'grid-3x3':
    '<rect width="18" height="18" x="3" y="3" rx="2"/>' +
    '<path d="M3 9h18"/>' +
    '<path d="M3 15h18"/>' +
    '<path d="M9 3v18"/>' +
    '<path d="M15 3v18"/>',
  calculator:
    '<rect width="16" height="20" x="4" y="2" rx="2"/>' +
    '<line x1="8" x2="16" y1="6" y2="6"/>' +
    '<line x1="16" x2="16" y1="14" y2="18"/>' +
    '<path d="M16 10h.01"/>' +
    '<path d="M12 10h.01"/>' +
    '<path d="M8 10h.01"/>' +
    '<path d="M12 14h.01"/>' +
    '<path d="M8 14h.01"/>' +
    '<path d="M12 18h.01"/>' +
    '<path d="M8 18h.01"/>',
  spline:
    '<circle cx="19" cy="5" r="2"/>' +
    '<circle cx="5" cy="19" r="2"/>' +
    '<path d="M5 17A12 12 0 0 1 17 5"/>',
  lightbulb:
    '<path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/>' +
    '<path d="M9 18h6"/>' +
    '<path d="M10 22h4"/>',
  minus: '<path d="M5 12h14"/>',
  target:
    '<circle cx="12" cy="12" r="10"/>' +
    '<circle cx="12" cy="12" r="6"/>' +
    '<circle cx="12" cy="12" r="2"/>',
  crosshair:
    '<circle cx="12" cy="12" r="10"/>' +
    '<line x1="22" x2="18" y1="12" y2="12"/>' +
    '<line x1="6" x2="2" y1="12" y2="12"/>' +
    '<line x1="12" x2="12" y1="6" y2="2"/>' +
    '<line x1="12" x2="12" y1="22" y2="18"/>',
  anchor:
    '<path d="M12 22V8"/>' +
    '<path d="M5 12H2a10 10 0 0 0 20 0h-3"/>' +
    '<circle cx="12" cy="5" r="3"/>',
  'trash-2':
    '<path d="M3 6h18"/>' +
    '<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>' +
    '<path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>' +
    '<line x1="10" x2="10" y1="11" y2="17"/>' +
    '<line x1="14" x2="14" y1="11" y2="17"/>',
  'rotate-cw':
    '<path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>' +
    '<path d="M21 3v5h-5"/>',
  trophy:
    '<path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>' +
    '<path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>' +
    '<path d="M4 22h16"/>' +
    '<path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>' +
    '<path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>' +
    '<path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>',
  'audio-lines':
    '<path d="M2 10v3"/>' +
    '<path d="M6 6v11"/>' +
    '<path d="M10 3v18"/>' +
    '<path d="M14 8v7"/>' +
    '<path d="M18 5v13"/>' +
    '<path d="M22 10v3"/>',
  'volume-2':
    '<path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.384 3.384A.705.705 0 0 0 11 19.298z"/>' +
    '<path d="M16 9a5 5 0 0 1 0 6"/>' +
    '<path d="M19.364 18.364a9 9 0 0 0 0-12.728"/>',
  'volume-x':
    '<path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.384 3.384A.705.705 0 0 0 11 19.298z"/>' +
    '<line x1="22" x2="16" y1="9" y2="15"/>' +
    '<line x1="16" x2="22" y1="9" y2="15"/>',
  play: '<polygon points="6 3 20 12 6 21 6 3"/>',
  quote:
    '<path d="M16 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z"/>' +
    '<path d="M5 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  'circle-check':
    '<circle cx="12" cy="12" r="10"/>' +
    '<path d="m9 12 2 2 4-4"/>',
  hash:
    '<line x1="4" x2="20" y1="9" y2="9"/>' +
    '<line x1="4" x2="20" y1="15" y2="15"/>' +
    '<line x1="10" x2="8" y1="3" y2="21"/>' +
    '<line x1="16" x2="14" y1="3" y2="21"/>',
  sigma:
    '<path d="M18 7V5a1 1 0 0 0-1-1H6.5a.5.5 0 0 0-.4.8l6.31 7.51a1 1 0 0 1 0 1.34L6.1 21.2a.5.5 0 0 0 .4.8H17a1 1 0 0 0 1-1v-2"/>',
};

/**
 * Build an SVG element for a Lucide icon.
 *
 * The element renders at `size` x `size` with `currentColor` strokes,
 * so consumers control colour via plain CSS `color`.
 */
export function iconSvg(name: LucideName, size: number = 24): SVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('xmlns', SVG_NS);
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.classList.add('gv-icon', `gv-icon-${name}`);
  svg.innerHTML = ICON_PATHS[name];
  return svg;
}
